import { useParams } from "react-router-dom";
import { useState } from "react";
import { api, apiError } from "@/api";
import { Seo } from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const pages = {
  about: {
    title: "The Manifesto",
    eyebrow: "About mellowjmedia",
    intro: "A photography practice built around machines, motion and the people who gather around them.",
    sections: [
      ["01 / Point of view", "mellowjmedia documents motorcycles, cars and mixed-machine events with a cinematic eye: long shadows, metallic highlights, deliberate negative space and frames that feel composed rather than captured by accident."],
      ["02 / Event coverage", "We build event-based albums so riders, drivers and collectors can find the machine they know in the context of the day it was seen."],
      ["03 / Digital craft", "Public previews are protected with a restrained watermark. Purchased files are clean, high-resolution digital downloads suitable for personal archives, prints and social sharing within the license terms."],
    ],
  },
  licensing: {
    title: "Licensing",
    eyebrow: "Usage rights",
    intro: "Clear terms for personal digital photography purchases.",
    sections: [
      ["Included", "Each purchase includes a non-exclusive personal license for digital display, personal printing, social sharing and portfolio use by the pictured vehicle owner or event participant."],
      ["Not included", "Commercial advertising, resale, stock redistribution, merchandise, brand campaigns and editorial syndication require a separate written commercial license."],
      ["Credit", "Credit to mellowjmedia is appreciated where practical. The watermark may not be removed from unpurchased previews."],
    ],
  },
  faq: {
    title: "Questions",
    eyebrow: "Collector FAQ",
    intro: "Everything you need to know before purchasing a frame or full album.",
    sections: [
      ["How do downloads arrive?", "After Stripe confirms payment, download links appear immediately on the success page. Signed-in customers can also access links from the dashboard for 7 days."],
      ["What is the difference between a photo and album?", "A single photo delivers one high-resolution original. A full album delivers every active high-resolution photo in that event as a zip archive."],
      ["Are previews watermarked?", "Yes. Public previews carry visible mellowjmedia protection. Paid originals are delivered without the preview watermark."],
      ["Can I check out as a guest?", "Yes. Accounts are optional and only needed to keep purchases in a persistent dashboard."],
    ],
  },
  terms: {
    title: "Terms of Service",
    eyebrow: "Store terms",
    intro: "The terms governing use of the mellowjmedia website and digital photography store.",
    sections: [
      ["Digital products", "Photos and albums are digital goods delivered through secure download links after confirmed payment."],
      ["Availability", "Event albums and pricing may change as new photos are processed or older events are archived."],
      ["Acceptable use", "Website content may not be scraped, resold, misrepresented or used to train machine-learning systems without written permission."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Data practice",
    intro: "We collect only the information needed to operate the photography store.",
    sections: [
      ["Information collected", "Account details, contact form messages, order records and payment status are stored to provide downloads and support."],
      ["Payments", "Card details are processed directly by Stripe and are never stored by mellowjmedia."],
      ["Retention", "Order records are retained for accounting and customer support. Download links expire after 7 days."],
    ],
  },
  refunds: {
    title: "Refund Policy",
    eyebrow: "Digital goods",
    intro: "Digital photography purchases are generally final once download links have been issued.",
    sections: [
      ["Before delivery", "If payment succeeds but no download link is issued, contact us and we will restore delivery or refund the order."],
      ["File issues", "Corrupt, incomplete or incorrect files will be replaced as quickly as possible."],
      ["Duplicate purchases", "Accidental duplicate orders can be reviewed for refund when the duplicate files have not been downloaded."],
    ],
  },
};

export default function StaticPage() {
  const { page } = useParams();
  const content = pages[page] || pages.about;
  return (
    <div data-testid={`${page}-page`} className="min-h-screen bg-onyx pt-32">
      <Seo title={content.title} description={content.intro} />
      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-8 lg:px-12">
        <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">{content.eyebrow}</p>
        <h1 className="mt-6 font-display text-5xl font-light leading-none text-white sm:text-7xl"><MaskedLine>{content.title}</MaskedLine></h1>
        <p className="mt-8 max-w-2xl text-lg leading-8 text-zinc-400">{content.intro}</p>
        {page === "contact" ? <ContactForm /> : (
          <div className="mt-16 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {content.sections.map(([title, body], index) => <Reveal key={title} delay={index * 0.08} className="bg-carbon p-8"><h2 className="font-lux text-lg uppercase tracking-[0.18em] text-white">{title}</h2><p className="mt-5 text-sm leading-7 text-zinc-500">{body}</p></Reveal>)}
          </div>
        )}
      </section>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We will reply soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) { toast.error(apiError(error)); } finally { setSending(false); }
  }

  return (
    <div className="mt-16 grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
      <div className="border border-white/10 bg-carbon p-8">
        <h2 className="font-display text-3xl text-white">Commissions & support</h2>
        <p className="mt-5 text-sm leading-7 text-zinc-500">Ask about an order, request commercial licensing, or discuss event coverage for motorcycle and automotive gatherings.</p>
        <div className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-gold">studio@mellowjmedia.com</div>
      </div>
      <form data-testid="contact-form" onSubmit={submit} className="grid gap-4 border border-white/10 bg-carbon p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input data-testid="contact-name-input" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 border-white/10 bg-white/[0.03]" />
          <Input data-testid="contact-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 border-white/10 bg-white/[0.03]" />
        </div>
        <Input data-testid="contact-subject-input" required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-12 border-white/10 bg-white/[0.03]" />
        <Textarea data-testid="contact-message-input" required placeholder="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="min-h-40 border-white/10 bg-white/[0.03]" />
        <Button data-testid="contact-submit-button" disabled={sending} className="h-12 rounded-none bg-gold font-mono text-xs uppercase tracking-[0.22em] text-onyx hover:bg-titanium">{sending ? "Sending" : "Send message"}</Button>
      </form>
    </div>
  );
}
